import {
	getNumAttempted,
	getTitleForType,
	getTopicsSectionLabel,
} from "@helpers/content/tag";
import { useAppStore } from "@hooks/useAppStore";
import type { Content, MixedItemTag } from "@memorang/types";
import type { TagsByType } from "@memorang/types";
import { t } from "i18next";

const buildSectionTitle = (
	tagCount: number,
	label: string,
	itemCount: number,
): string =>
	useAppStore.getState().app?.hideTotalItemCounts
		? t("dashboard.tagSections.summaryWithoutItemCount", {
				tagCount,
				label,
			})
		: t("dashboard.tagSections.summary", {
				tagCount,
				label,
				itemCount,
			});

export const getTagTotalResponses = (tag: MixedItemTag): number =>
	tag.totalResponses ||
	(tag.children ?? []).reduce(
		(total, child) => total + getTagTotalResponses(child),
		0,
	);

const getResponsesCountDescription = (tag: MixedItemTag): string =>
	t("dashboard.tagSections.responseCount", {
		count: getTagTotalResponses(tag),
	});

/**
 * Swaps each tag's item-count description for its response volume when the app
 * hides total item counts.
 */
export const withResponseCountDescriptions = <
	T extends { tags: MixedItemTag[] },
>(
	sections: T[],
): T[] => {
	if (!useAppStore.getState().app?.hideTotalItemCounts) {
		return sections;
	}
	return sections.map((section) => ({
		...section,
		tags: section.tags.map((tag) => ({
			...tag,
			description: getResponsesCountDescription(tag),
		})),
	}));
};

export const getSectionTags = (
	numItems: number,
	tagsByType: TagsByType,
	tags: MixedItemTag[],
	handleShowPurchaseDialog: () => void,
	hasAccess?: boolean,
) => {
	const allTags = Object.keys(tagsByType).flatMap((key) => tagsByType[key]);

	const lockedTags = allTags.filter((item) =>
		hasAccess ? !item.locked : item.locked,
	);

	const unlockedTags = allTags.filter(({ locked }) => !locked);

	const numUnlockedTags = unlockedTags.length;
	const numLockedTags = lockedTags.length;

	const uniqueItemIds = new Set<string | number>();
	for (const tag of unlockedTags) {
		if (tag.items) {
			for (const item of tag.items) {
				uniqueItemIds.add(item.id);
			}
		}
	}
	const numUnlockedItems = uniqueItemIds.size;

	const lockedSectionItemsCount = hasAccess
		? numItems
		: numItems - numUnlockedItems;

	const sections = hasAccess
		? [
				{
					title: buildSectionTitle(
						numUnlockedTags,
						getTopicsSectionLabel(tags, numUnlockedTags),
						numItems,
					),
					tags: unlockedTags,
				},
			]
		: [
				{
					title: buildSectionTitle(
						numUnlockedTags,
						getTopicsSectionLabel(tags, numUnlockedTags),
						numItems - lockedSectionItemsCount,
					),
					chipVariant: lockedTags.length ? ("free" as const) : undefined,
					tags: unlockedTags,
				},
				...(lockedTags?.length
					? [
							{
								title: buildSectionTitle(
									numLockedTags,
									getTopicsSectionLabel(tags, numLockedTags),
									lockedSectionItemsCount,
								),
								chipVariant: "premium" as const,
								tags: lockedTags,
								action: handleShowPurchaseDialog,
							},
						]
					: []),
			];

	return [...sections];
};

/** A tag that may carry the free-allowance chip forwarded to MasteryListItem. */
type FreeAllowanceTag = MixedItemTag & { titleChip?: string };

/**
 * Re-describes unlocked tags in terms of the free allowance when a premium offering exists.
 * Runs after withResponseCountDescriptions, which would otherwise clobber the description.
 */
export const withFreeAllowanceDescriptions = <
	T extends { tags: FreeAllowanceTag[] },
>(
	sections: T[],
	{ hasPremiumOffering }: { hasPremiumOffering: boolean },
): (Omit<T, "tags"> & { tags: FreeAllowanceTag[] })[] => {
	if (!hasPremiumOffering) {
		return sections;
	}
	return sections.map((section) => ({
		...section,
		tags: section.tags.map((tag) =>
			tag.locked || !tag.numItems
				? tag
				: {
						...tag,
						titleChip: t("dashboard.skills.freeItemsIncludedChip"),
						description: t("dashboard.skills.freeItemsAnsweredSubtitle", {
							// getNumAttempted, not getTagTotalResponses: this subtitle promises a
							// denominator ("N of M free items answered"), and only the distribution-
							// derived attempted count is bounded by numItems (calculateTagDist buckets
							// items, so sum(distribution) === numItems). totalResponses is response
							// *volume* and can exceed numItems once a user re-answers an item, which
							// would render a nonsensical "12 of 10 free items answered".
							answered: getNumAttempted(tag.distribution),
							count: tag.numItems,
						}),
					},
		),
	}));
};

export const getStudyPackItemCount = (contents: Content[] = []): number =>
	contents
		.filter((content) => content.collectionType === "STUDY_PACK")
		.reduce((total, content) => total + content.totalCount, 0);

export const buildGroupSectionTitle = (
	type: string,
	tags: MixedItemTag[],
	numItems: number,
): string =>
	buildSectionTitle(tags.length, getTitleForType(type, tags.length), numItems);
